import { useState } from "react";
import { api, formatApiError, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { MonthNav } from "./MonthNav";
import { Pencil, Search, Target, Trash2, Lock } from "lucide-react";
import PaginationControls from "../PaginationControls";
import { usePagination } from "@/hooks/usePagination";

/* ================================================================
   ============= TAB: BUKU KAS (Debet/Kredit) =============
   Konvensi kustom (permintaan user):
   - KREDIT = pemasukan / pengisian saldo kas (kas bertambah)
   - DEBET  = pengeluaran (kas berkurang)
   - Saldo berjalan = saldo sebelumnya + Kredit − Debet
   Data sumber sama dengan Jurnal Akuntansi — berbeda di akun pemasukan.
   ================================================================ */
export function BookTab({ month, setMonth, monthLabel, prevMonthLabel, shiftMonth, search, setSearch, txData, filtered, loading, onEdit, onRemove, kasbonOpen, isOpenKasbon, onCashChanged }) {
  const [showAdjustOnly, setShowAdjustOnly] = useState(false);
  // Saldo Awal diambil dari backend (auto carry-over + override via menu "Kunci Saldo Bulan").
  // Kalau ada override langsung untuk bulan ini → pakai itu.
  // Kalau tidak → cascade otomatis dari closing bulan sebelumnya.
  const isForcedOpening = false; // dipertahankan untuk backward-compat rendering
  // Buku Kas (tab): HARD FILTER — hanya transaksi akun 101 Kas Utama.
  const kasTxAll = filtered;
  const openingBalance = Number(txData.opening_balance || 0);
  const adjustCount = kasTxAll.filter((t) => t.reference === "ADJUSTMENT").length;

  // Purge ALL adjustment transactions — nuclear cleanup.
  // Backend akan hapus semua cash_transactions dgn reference="ADJUSTMENT".
  const purgeAdjustments = async () => {
    if (adjustCount === 0) {
      toast.info("Tidak ada jurnal penyesuaian untuk dihapus.");
      return;
    }
    const msg1 = `HAPUS SEMUA transaksi penyesuaian saldo?\n\nAksi ini akan menghapus ${adjustCount} jurnal dengan ref='ADJUSTMENT' di bulan ini + bulan-bulan lain.\n\nSetelah dihapus, saldo real-time akan BERUBAH. Anda dapat set ulang Saldo Awal manual via tombol "Saldo Awal" di atas.\n\nLanjutkan?`;
    if (!window.confirm(msg1)) return;
    const c = window.prompt('Ketik "HAPUS PENYESUAIAN" untuk konfirmasi:');
    if (c !== "HAPUS PENYESUAIAN") { toast.error("Dibatalkan — konfirmasi tidak cocok."); return; }
    try {
      const res = await api.post("/cashbook/purge-adjustments");
      toast.success(`${res.data.deleted_count} jurnal penyesuaian dihapus. Silakan cek Saldo Awal.`);
      if (onCashChanged) await onCashChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal");
    }
  };
  const kasTx = showAdjustOnly ? kasTxAll.filter((t) => t.reference === "ADJUSTMENT") : kasTxAll;
  // Recompute running balance: Saldo Awal + Kredit − Debet (per baris)
  // NOTE: Saldo dihitung dari FULL kasTxAll (bukan filtered) supaya angka Saldo tetap akurat
  //       ketika user filter "Adjustment Only".
  const jurnalAll = (() => {
    let running = openingBalance;
    return kasTxAll.map((t) => {
      running = t.type === "in" ? running + Number(t.amount || 0) : running - Number(t.amount || 0);
      return { ...t, balance: running };
    });
  })();
  const jurnal = showAdjustOnly ? jurnalAll.filter((t) => t.reference === "ADJUSTMENT") : jurnalAll;
  const totalKredit = kasTx.reduce((s, t) => s + (t.type === "in" ? Number(t.amount) : 0), 0);
  const totalDebet = kasTx.reduce((s, t) => s + (t.type === "out" ? Number(t.amount) : 0), 0);
  // Kasbon Belum Lunas — dianggap pengurang saldo kas nyata (uang sudah dikeluarkan tapi belum lunas).
  // STRICT filter via isOpenKasbon: hanya status === 'PENDING' (backend normalized).
  // Kasbon LUNAS/PAID otomatis tersembunyi — hanya BELUM LUNAS yang tampil di sini.
  const kasbonList = (kasbonOpen?.items || []).filter(isOpenKasbon).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const kasbonTotal = kasbonList.reduce((s, k) => s + Number(k.amount || 0), 0);
  const runningAfterTx = jurnal.length > 0 ? jurnal[jurnal.length - 1].balance : openingBalance;
  const kasbonRows = (() => {
    let running = runningAfterTx;
    return kasbonList.map((k) => {
      running = running - Number(k.amount || 0);
      return { ...k, running_balance: running };
    });
  })();
  const closingBalance = openingBalance + totalKredit - totalDebet - kasbonTotal;
  const journalDisplayRows = [
    ...jurnal.map((transaction) => ({ kind: "transaction", data: transaction })),
    ...(!showAdjustOnly ? kasbonRows.map((kasbon) => ({ kind: "kasbon", data: kasbon })) : []),
  ];
  const {
    page,
    pageSize,
    totalItems,
    totalPages,
    paginatedItems,
    goToPage,
  } = usePagination(journalDisplayRows, {
    resetKey: `${month}:${search}:${showAdjustOnly}`,
  });
  const paginatedJournalItems = paginatedItems
    .filter((row) => row.kind === "transaction")
    .map((row) => row.data);
  const paginatedKasbonItems = paginatedItems
    .filter((row) => row.kind === "kasbon")
    .map((row) => row.data);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <MonthNav value={month} onChange={setMonth} testIdPrefix="journal-month" prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} />
          <div className="relative flex-1 md:flex-none">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari keterangan / akun / ref…"
              data-testid="journal-search"
              className="rounded-none border border-zinc-300 bg-white pl-10 pr-3 py-2 text-sm w-full md:w-80 focus:border-[#002FA7] focus:outline-none"
            />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#002FA7]/10 text-[#002FA7] px-2.5 py-1.5 border border-[#002FA7]/30 whitespace-nowrap hidden md:inline">Kredit: Akun 101 · Debet: Semua Akun</span>
          {adjustCount > 0 && (
            <>
              <button
                type="button"
                data-testid="filter-adjustment-toggle"
                onClick={() => setShowAdjustOnly((v) => !v)}
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 border inline-flex items-center gap-1.5 whitespace-nowrap ${
                  showAdjustOnly
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-[#002FA7] border-[#002FA7]/40 hover:bg-[#002FA7]/5"
                }`}
                title={showAdjustOnly ? "Klik untuk tampilkan semua transaksi" : "Filter hanya jurnal penyesuaian"}
              >
                <Target className="w-3 h-3" />
                {showAdjustOnly ? `Adjustment Only (${adjustCount})` : `Adjustment: ${adjustCount}`}
              </button>
              <button
                type="button"
                data-testid="purge-adjustments-btn"
                onClick={purgeAdjustments}
                className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 border inline-flex items-center gap-1.5 whitespace-nowrap bg-white text-[#E81123] border-[#E81123]/40 hover:bg-[#E81123]/5"
                title="Hapus SEMUA jurnal penyesuaian"
              >
                <Trash2 className="w-3 h-3" /> <span className="hidden sm:inline">Hapus Semua Penyesuaian</span><span className="sm:hidden">Hapus Penyesuaian</span>
              </button>
            </>
          )}
        </div>
        <div className="text-[11px] md:text-xs text-zinc-500 font-mono w-full md:w-auto">
          {jurnal.length} jurnal{showAdjustOnly ? " (penyesuaian saja)" : ""} · Debet <b className="text-[#E81123]">{formatIDR(totalDebet)}</b> · Kredit <b className="text-[#008A00]">{formatIDR(totalKredit)}</b>
          {kasbonList.length > 0 && !showAdjustOnly && (
            <> · Kasbon <b className="text-[#F97316]">{formatIDR(kasbonTotal)}</b></>
          )}
        </div>
      </div>

      <div className="hidden md:block border border-zinc-900 bg-white overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-zinc-900 text-white text-[11px] font-bold uppercase tracking-widest">
              <th className="px-3 py-3 border-r border-zinc-700">Kode Akun</th>
              <th className="px-3 py-3 border-r border-zinc-700">Nama Akun</th>
              <th className="px-3 py-3 border-r border-zinc-700 whitespace-nowrap">Tanggal</th>
              <th className="px-3 py-3 border-r border-zinc-700">Keterangan</th>
              <th className="px-3 py-3 text-right border-r border-zinc-700 bg-[#E81123]/90">Debet</th>
              <th className="px-3 py-3 text-right border-r border-zinc-700 bg-[#008A00]/90">Kredit</th>
              <th className="px-3 py-3 text-right border-r border-zinc-700">Saldo</th>
              <th className="px-3 py-3 text-center whitespace-nowrap">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-zinc-200 bg-[#002FA7]/5">
              <td colSpan={4} className="px-3 py-2.5">
                <span className="text-xs font-bold uppercase tracking-widest text-[#002FA7]">Saldo Akhir {prevMonthLabel(month)}</span>
              </td>
              <td className="px-3 py-2.5"></td>
              <td className="px-3 py-2.5"></td>
              <td className="px-3 py-2.5 text-right font-mono font-bold text-[#002FA7]" data-testid="prev-month-closing">
                {formatIDR(openingBalance)}
              </td>
              <td className="px-3 py-2.5"></td>
            </tr>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400 font-mono text-xs">Memuat…</td></tr>}
            {!loading && journalDisplayRows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400 font-mono text-xs">Belum ada arus kas bulan ini.</td></tr>
            )}
            {paginatedJournalItems.map((t) => {
              const isAdjustment = t.reference === "ADJUSTMENT";
              const rowBg = isAdjustment
                ? (t.type === "in" ? "bg-[#008A00]/10" : "bg-[#E81123]/10")
                : (t.auto ? "bg-amber-50/40" : "");
              return (
              <tr key={t.id} data-testid="journal-row" data-adjustment={isAdjustment ? "true" : "false"} className={`border-b border-zinc-100 hover:bg-zinc-50 ${rowBg}`}>
                <td className="px-3 py-2.5 font-mono text-xs font-bold text-zinc-700 whitespace-nowrap">{t.account_code}</td>
                <td className="px-3 py-2.5 text-xs">
                  {t.account_code === "101" ? "Kas" : t.account_name}
                  {t.auto && <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-amber-700 inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Auto</span>}
                  {isAdjustment && (
                    <span className={`ml-2 text-[9px] uppercase tracking-widest font-bold inline-flex items-center gap-1 px-1.5 py-0.5 border ${
                      t.type === "in"
                        ? "bg-[#008A00] text-white border-[#008A00]"
                        : "bg-[#E81123] text-white border-[#E81123]"
                    }`}>
                      <Target className="w-2.5 h-2.5" /> Penyesuaian
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{t.date}</td>
                <td className="px-3 py-2.5 text-xs">
                  <div>{t.description}</div>
                  {t.reference && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: {t.reference}</div>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs bg-[#E81123]/5">
                  {t.type === "out" ? <span className="text-[#E81123] font-bold">{formatIDR(t.amount)}</span> : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs bg-[#008A00]/5">
                  {t.type === "in" ? <span className="text-[#008A00] font-bold">{formatIDR(t.amount)}</span> : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-bold text-zinc-900">{formatIDR(t.balance)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <div className="flex items-center justify-center gap-1">
                    {t._virtual ? (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 italic px-2">Sistem</span>
                    ) : (
                      <>
                        <button
                          data-testid={`journal-edit-${t.id}`}
                          onClick={() => onEdit && onEdit(t)}
                          title={t.auto ? "Transaksi otomatis — edit tidak disarankan" : "Edit transaksi"}
                          className="p-1.5 hover:bg-[#002FA7]/10 text-[#002FA7]"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`journal-del-${t.id}`}
                          onClick={() => onRemove && onRemove(t)}
                          title="Hapus transaksi"
                          className="p-1.5 hover:bg-[#E81123]/10 text-[#E81123]"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {paginatedKasbonItems.length > 0 && (
              <>
                <tr className="border-t border-dashed border-[#F97316]/40 bg-[#F97316]/5">
                  <td colSpan={8} className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[#F97316]">
                    · Kasbon Sementara (belum lunas)
                  </td>
                </tr>
                {paginatedKasbonItems.map((k) => (
                  <tr key={`kasbon-${k.id}`} data-testid="journal-kasbon-row" className="border-b border-zinc-100 hover:bg-[#F97316]/10 bg-[#F97316]/5">
                    <td className="px-3 py-2.5 font-mono text-xs font-bold text-[#F97316] whitespace-nowrap">KASBON</td>
                    <td className="px-3 py-2.5 text-xs">
                      Kasbon · {k.name}
                      <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-[#F97316]">pending</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{k.date}</td>
                    <td className="px-3 py-2.5 text-xs">
                      <div className="text-zinc-700">{k.description || "Uang muka staff — belum dilunasi"}</div>
                      <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: KASBON-{String(k.id).slice(0, 8)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs bg-[#F97316]/10">
                      <span className="text-[#F97316] font-bold">{formatIDR(k.amount)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs bg-[#008A00]/5">
                      <span className="text-zinc-300">—</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-bold text-[#F97316]">{formatIDR(k.running_balance)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="text-[9px] font-mono uppercase text-zinc-400">tab Kasbon</span>
                    </td>
                  </tr>
                ))}
              </>
            )}
            {!loading && (jurnal.length > 0 || isForcedOpening) && (
              <>
                <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                  <td colSpan={4} className="px-3 py-3">
                    <span className="text-xs font-bold uppercase tracking-widest text-zinc-900">Total Kas Utama (sebelum Kasbon)</span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-[#E81123]">{formatIDR(totalDebet)}</td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-[#008A00]">
                    {formatIDR(totalKredit + (isForcedOpening ? openingBalance : 0))}
                    {isForcedOpening && (
                      <div className="text-[10px] font-normal text-zinc-500 mt-0.5">
                        (Kredit {formatIDR(totalKredit)} + Saldo Awal {formatIDR(openingBalance)})
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-zinc-900">
                    {formatIDR(openingBalance + totalKredit - totalDebet)}
                  </td>
                  <td className="px-3 py-3"></td>
                </tr>
                {kasbonTotal > 0 && (
                  <tr className="bg-[#F97316]/10 border-b border-[#F97316]/30">
                    <td colSpan={4} className="px-3 py-2.5">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#F97316]">− Kasbon Belum Lunas</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-[#F97316]">{formatIDR(kasbonTotal)}</td>
                    <td className="px-3 py-2.5"></td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-[#F97316]">−{formatIDR(kasbonTotal)}</td>
                    <td className="px-3 py-2.5"></td>
                  </tr>
                )}
                <tr className="border-t-2 border-zinc-900 bg-zinc-900 text-white">
                  <td colSpan={6} className="px-3 py-3">
                    <span className="text-xs font-bold uppercase tracking-widest">Saldo Akhir (setelah Kasbon)</span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-lg" data-testid="journal-closing-balance">{formatIDR(closingBalance)}</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        <div className="bg-[#002FA7]/5 border border-[#002FA7]/30 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Saldo Awal {monthLabel(month)}</div>
              <div className="text-[10px] text-zinc-500 font-mono">Akun 101 · pindahan dari bulan sebelumnya</div>
            </div>
            <div className="font-mono font-bold text-sm text-[#002FA7]">{formatIDR(openingBalance)}</div>
          </div>
        </div>

        {loading && (
          <div className="text-center text-zinc-400 text-xs font-mono py-8">Memuat…</div>
        )}
        {!loading && journalDisplayRows.length === 0 && (
          <div className="text-center text-zinc-400 text-xs font-mono py-8 border border-dashed border-zinc-200">
            Belum ada arus kas bulan ini.
          </div>
        )}
        {!loading && paginatedJournalItems.map((t) => (
          <div key={t.id} data-testid="mobile-journal-card" className={`border border-zinc-200 bg-white p-3 ${t.reference === "ADJUSTMENT" ? "border-l-4 border-l-[#002FA7]" : ""}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">{t.date}</span>
                <span className="font-mono text-[11px] font-bold text-zinc-800 bg-zinc-100 px-1.5 py-0.5">{t.account_code}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <button onClick={() => onEdit(t)} disabled={t.auto} className="p-1 hover:bg-zinc-100 text-zinc-700 disabled:opacity-30" data-testid="edit-journal-mobile"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => onRemove(t)} className="p-1 hover:bg-[#E81123]/10 text-[#E81123]" data-testid="del-journal-mobile"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="text-sm font-semibold text-zinc-900">{t.account_name}</div>
            <div className="text-xs text-zinc-600 mt-0.5 break-words">{t.description}</div>
            {t.reference && (
              <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: {t.reference}</div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-zinc-100 text-[11px] font-mono">
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Kredit</div>
                <div className="text-[#008A00] font-bold">{t.type === "in" ? formatIDR(t.amount) : "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Debet</div>
                <div className="text-[#E81123] font-bold">{t.type === "out" ? formatIDR(t.amount) : "—"}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase text-zinc-400">Saldo</div>
                <div className="text-zinc-900 font-bold">{formatIDR(t.balance)}</div>
              </div>
            </div>
          </div>
        ))}

        {!showAdjustOnly && paginatedKasbonItems.length > 0 && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#F97316] mt-2 mb-1">
              · Kasbon Sementara (belum lunas)
            </div>
            {paginatedKasbonItems.map((k) => (
              <div key={`m-kasbon-${k.id}`} data-testid="mobile-kasbon-card" className="border border-[#F97316]/40 bg-[#F97316]/5 p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[#F97316] whitespace-nowrap">{k.date}</span>
                    <span className="font-mono text-[11px] font-bold text-[#F97316] bg-white px-1.5 py-0.5 border border-[#F97316]/30">KASBON</span>
                  </div>
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[#F97316]">pending</span>
                </div>
                <div className="text-sm font-semibold text-zinc-900">Kasbon · {k.name}</div>
                <div className="text-xs text-zinc-600 mt-0.5 break-words">{k.description || "Uang muka staff — belum dilunasi"}</div>
                <div className="text-[10px] font-mono text-zinc-400 mt-0.5">ref: KASBON-{String(k.id).slice(0, 8)}</div>
                <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-[#F97316]/20 text-[11px] font-mono">
                  <div>
                    <div className="text-[9px] uppercase text-zinc-400">Kasbon</div>
                    <div className="text-[#F97316] font-bold">{formatIDR(k.amount)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-zinc-400">—</div>
                    <div className="text-zinc-300">—</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-zinc-400">Saldo</div>
                    <div className="text-[#F97316] font-bold">{formatIDR(k.running_balance)}</div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {!loading && jurnal.length > 0 && (
          <>
            <div className="bg-zinc-50 border-2 border-zinc-900 p-3 mt-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-800 font-bold mb-2">Total Kas Utama (sebelum Kasbon)</div>
              <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                <div>
                  <div className="text-[9px] uppercase text-zinc-400">Debet</div>
                  <div className="text-[#E81123] font-bold">{formatIDR(totalDebet)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-zinc-400">Kredit</div>
                  <div className="text-[#008A00] font-bold">{formatIDR(totalKredit)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-zinc-400">Saldo</div>
                  <div className="text-zinc-900 font-bold">{formatIDR(runningAfterTx)}</div>
                </div>
              </div>
            </div>
            {kasbonRows.length > 0 && !showAdjustOnly && (
              <div className="bg-[#F97316]/10 border border-[#F97316]/30 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-[#F97316] font-bold">− Kasbon Belum Lunas</div>
                  <div className="font-mono font-bold text-[#F97316]">−{formatIDR(kasbonTotal)}</div>
                </div>
              </div>
            )}
            <div className="bg-zinc-900 text-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">Saldo Akhir (setelah Kasbon)</div>
              <div className="font-mono font-bold text-lg mt-1" data-testid="mobile-journal-closing">{formatIDR(closingBalance)}</div>
            </div>
          </>
        )}
      </div>

      {!loading && (
        <PaginationControls
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={goToPage}
          testIdPrefix="cashbook-journal-pagination"
        />
      )}

      <div className="mt-3 text-[11px] text-zinc-500">
        <b>Konvensi:</b> Debet = pengeluaran · Kredit = pemasukan akun 101 · <b className="text-[#F97316]">Kasbon</b> = uang muka staff yg belum lunas (kas fisik berkurang meski belum jadi expense resmi). Rumus: <b>Saldo Akhir = Saldo Awal + Kredit − Debet − Kasbon Belum Lunas</b>.
        Auto-sync dari modul <b>Pembelian</b>, <b>Penjualan/Kasir</b>, <b>Kas Operasional</b>, dan <b>Kasbon Sementara</b>. Kasbon LUNAS tidak ditampilkan — kelola di tab <b>Kasbon Sementara</b>.
      </div>
    </div>
  );
}